import { Dimmer, Loader, Modal } from '@mairistem/composants';
import { getErrorMessage, type UserRole } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import type BaseFeuilleSaisie from '../../../../classes/feuilleSaisies/BaseFeuilleSaisie';
import TypePeriode from '../../../../constants/simulation/typePeriode';
import type TreeRow from '../../../../interfaces/feuilleSaisie/treeRow';
import type MontantSimulation from '../../../../interfaces/simulation/montantSimulation';
import type Simulation from '../../../../interfaces/simulation/simulation';
import { getMontantsByImputation } from '../../../../services/simulation/montantSimulation';
import {
	getImputationParams,
	getImputationText,
} from '../../../../utils/imputation';
import { shouldAddReport } from '../../../../utils/simulation/simulation';
import MontantsSimulationModalContent from './MontantsSimulationModalContent';

interface MontantsSimulationModalProps {
	feuilleSaisie: BaseFeuilleSaisie;
	isDemandeEnabled: boolean;
	onAddMontant: (nombre?: number) => void;
	onChangeCommentaire: (e: React.FocusEvent<HTMLInputElement>) => void;
	onChangeMontant: (
		differenceMontant: number,
		typePeriode: TypePeriode,
		nameColumn: string,
	) => void;
	onClose: () => void;
	// Montants de la ligne relus en base a la fermeture, pour recaler le
	// TreeView (un autre utilisateur a pu les modifier entre-temps)
	onRecalageMontants: (montantsSimulation: MontantSimulation[]) => void;
	onRemoveMontant: () => void;
	open: boolean;
	rowData: TreeRow;
	simulation: Simulation;
	userRole: UserRole;
}

const MontantsSimulationModal = ({
	feuilleSaisie,
	isDemandeEnabled,
	onAddMontant,
	onChangeCommentaire,
	onChangeMontant,
	onClose,
	onRecalageMontants,
	onRemoveMontant,
	open,
	rowData,
	simulation,
	userRole,
}: MontantsSimulationModalProps) => {
	const showRar =
		feuilleSaisie.isCreditReporteShown &&
		shouldAddReport(simulation, feuilleSaisie);

	// Au CA, il n'y a plus de notion de demande a arbitrer
	const isCA = simulation.typePeriode === TypePeriode.CA_CFU;

	const [modalLoading, setModalLoading] = React.useState(true);
	const [montantsSimulation, setMontantsSimulation] = React.useState<
		MontantSimulation[]
	>([]);
	const [fermetureEnCours, setFermetureEnCours] = React.useState(false);
	// Sauvegardes lancees depuis la modal et pas encore terminees
	const sauvegardesEnCours = React.useRef(new Set<Promise<void>>());
	// Dernier callback recu : celui capture au clic de fermeture peut etre
	// obsolete une fois les sauvegardes en cours terminees (elles mettent
	// elles-memes a jour le TreeView)
	const onRecalageMontantsRef = React.useRef(onRecalageMontants);
	onRecalageMontantsRef.current = onRecalageMontants;

	const suivreSauvegarde = React.useCallback((sauvegarde: Promise<void>) => {
		const retirer = () => sauvegardesEnCours.current.delete(sauvegarde);
		sauvegardesEnCours.current.add(sauvegarde);
		sauvegarde.then(retirer, retirer);
	}, []);

	const getParamsImputation = () =>
		new URLSearchParams({
			...getImputationParams(simulation.identifiant, rowData),
			withEtiquettes: 'true',
		});

	const fetchMontantsSimulation = async () => {
		try {
			const { data } = await getMontantsByImputation(getParamsImputation());
			// Tri par date de creation une fois pour toutes : les nouveaux
			// montants (creation/duplication) sont ensuite systematiquement
			// ajoutes en tete de liste, sans qu'il soit necessaire de retrier
			setMontantsSimulation(
				[...data].sort(
					(a, b) =>
						new Date(a.dateCreation).getTime() -
						new Date(b.dateCreation).getTime(),
				),
			);
		} catch (e) {
			toast.error(
				getErrorMessage(
					e,
					'Erreur lors du chargement des montants de simulation',
				),
			);
		} finally {
			setModalLoading(false);
		}
	};

	// Avant de fermer, on relit les montants de la ligne pour recaler le
	// TreeView : la mise a jour par difference (cf onChangeMontant) ne voit pas
	// les modifications faites par un autre utilisateur pendant que la modal
	// etait ouverte
	const handleClose = async () => {
		if (fermetureEnCours) return;

		setFermetureEnCours(true);
		setModalLoading(true);
		try {
			// La sauvegarde d'un champ (au blur) precede le clic de fermeture : on
			// l'attend pour ne pas relire un etat anterieur a la derniere saisie
			await Promise.allSettled(sauvegardesEnCours.current);
			const { data } = await getMontantsByImputation(getParamsImputation());
			onRecalageMontantsRef.current(data);
		} catch (e) {
			toast.error(
				getErrorMessage(
					e,
					'Erreur lors de la mise à jour des montants de la ligne budgétaire',
				),
			);
		} finally {
			onClose();
		}
	};

	return (
		<Modal
			closeIcon
			onClose={handleClose}
			onMount={fetchMontantsSimulation}
			open={open}
			size={isCA ? 'large' : 'fullscreen'}
		>
			<Modal.Header
				content={
					isCA
						? `Détail de la ligne budgétaire ${getImputationText(rowData)}`
						: `Détail des ${isDemandeEnabled ? 'demandes' : 'montants'} pour la ligne budgétaire ${getImputationText(rowData)}`
				}
			/>
			<Modal.Content
				className="montantsSimulationModalModalContent"
				scrolling={false}
			>
				{modalLoading ? (
					<Dimmer active inverted>
						<Loader active />
					</Dimmer>
				) : (
					<MontantsSimulationModalContent
						anneeExercice={simulation.exercice.anneeExercice}
						identifiantEntite={simulation.identifiantEntite}
						identifiantExercice={simulation.identifiantExercice}
						identifiantSimulation={simulation.identifiant}
						isDemandeEnabled={isDemandeEnabled}
						libelleBudget={simulation.exercice.entite?.entite?.libelle}
						montantsSimulation={montantsSimulation}
						onAddMontant={onAddMontant}
						onChangeCommentaire={onChangeCommentaire}
						onChangeMontant={onChangeMontant}
						onRemoveMontant={onRemoveMontant}
						rowData={rowData}
						saisieBudgetService={Boolean(
							simulation.exercice.saisieBudgetService,
						)}
						setMontantsSimulation={setMontantsSimulation}
						showRar={showRar}
						suivreSauvegarde={suivreSauvegarde}
						transfertCpta={simulation.transfertCpta}
						typePeriode={simulation.typePeriode}
						userRole={userRole}
					/>
				)}
			</Modal.Content>
		</Modal>
	);
};

export default MontantsSimulationModal;
